import {
  BASIC_TEMPLATE_VALID_KEYS,
  CA_TAX_TEMPLATE_REQUIRED_KEYS,
  CA_TAX_VALID_HEADER_KEYS,
  CSV_CA_TAX_TEMPLATE_HEADERS,
  CSV_HEADER_TO_KEY_MAP,
  CSV_TEMPLATE_HEADERS,
  CSV_US_TAX_TEMPLATE_HEADERS,
  CsvHeaderKey,
  US_TAX_TEMPLATE_REQUIRED_KEYS,
  US_TAX_VALID_HEADER_KEYS,
} from '@/utils/constants';
import AppRegion from '@/types/app-region';

export type TemplateConfig = {
  templateHeaders: string[];
  validHeaderSet: Set<CsvHeaderKey>;
  taxSpecificKeys: Set<CsvHeaderKey> | null;
};

/**
 * Normalizes a header to its canonical key by:
 * 1. Converting to lowercase for case-insensitive matching
 * 2. Normalizing curly apostrophes to straight apostrophes
 * 3. Removing format hints like "(YYYY-MM-DD)" or "($)"
 * 4. Trimming whitespace
 * 5. Mapping to a CsvHeaderKey enum value via CSV_HEADER_TO_KEY_MAP
 */
export const normalizeHeaderToKey = (header: string): CsvHeaderKey | null => {
  const normalized = header
    .toLowerCase()
    .replace(/['']/g, "'") // Normalize curly apostrophes to straight
    .replace(/\([^)]*\)/g, '') // Remove anything in parentheses
    .trim();
  return CSV_HEADER_TO_KEY_MAP[normalized] || null;
};

/**
 * Returns the template configuration based on region and tax analysis access.
 */
export const getTemplateConfigForRegion = (appRegion: AppRegion | null | undefined, isTaxAnalysisEnabled: boolean): TemplateConfig => {
  if (!isTaxAnalysisEnabled) {
    return {
      templateHeaders: CSV_TEMPLATE_HEADERS,
      validHeaderSet: BASIC_TEMPLATE_VALID_KEYS,
      taxSpecificKeys: null,
    };
  }

  if (appRegion === AppRegion.CAN) {
    return {
      templateHeaders: CSV_CA_TAX_TEMPLATE_HEADERS,
      validHeaderSet: CA_TAX_VALID_HEADER_KEYS,
      taxSpecificKeys: CA_TAX_TEMPLATE_REQUIRED_KEYS,
    };
  }

  if (appRegion === AppRegion.US) {
    return {
      templateHeaders: CSV_US_TAX_TEMPLATE_HEADERS,
      validHeaderSet: US_TAX_VALID_HEADER_KEYS,
      taxSpecificKeys: US_TAX_TEMPLATE_REQUIRED_KEYS,
    };
  }

  return {
    templateHeaders: CSV_TEMPLATE_HEADERS,
    validHeaderSet: BASIC_TEMPLATE_VALID_KEYS,
    taxSpecificKeys: null,
  };
};

/**
 * Determines if the CSV upload should be treated as a tax template.
 * Checks both original headers and mapped template headers.
 */
export const detectTaxTemplate = (
  headers: string[],
  templateHeaders: string[],
  appRegion: AppRegion | null | undefined,
  isTaxAnalysisEnabled: boolean,
): boolean => {
  if (!isTaxAnalysisEnabled || !headers || headers.length === 0) {
    return false;
  }

  const { taxSpecificKeys } = getTemplateConfigForRegion(appRegion, isTaxAnalysisEnabled);

  if (!taxSpecificKeys) {
    return false;
  }

  // Check which template columns would be mapped based on number of CSV columns
  // Note: The UI maps columns sequentially (column 0 → template[0], column 1 → template[1], etc.)
  // This works for both flows: direct upload (headers match template) and DEFINE_HEADERS (user maps columns)
  const mappedTemplateHeaders = templateHeaders.slice(0, headers.length);
  const mappedTemplateHeaderKeys = new Set(mappedTemplateHeaders.map(normalizeHeaderToKey).filter((key): key is CsvHeaderKey => key !== null));

  return Array.from(taxSpecificKeys).some(key => mappedTemplateHeaderKeys.has(key));
};

/**
 * Validates that all CSV headers can be mapped to known template headers.
 */
export const validateHeadersMatch = (fields: string[], validHeaderSet: Set<CsvHeaderKey>): boolean => {
  if (!fields || fields.length === 0) {
    return false;
  }

  const csvHeaderKeys = new Set(fields.map(normalizeHeaderToKey).filter((key): key is CsvHeaderKey => key !== null));

  // Verify that:
  // 1. All CSV fields were successfully mapped to known headers (fields.length === csvHeaderKeys.size)
  // 2. Each mapped header is valid for this upload type (every key in validHeaderSet)
  return fields.length === csvHeaderKeys.size && Array.from(csvHeaderKeys).every(key => validHeaderSet.has(key));
};
